import { memo, useEffect, useMemo, useRef, useState } from "react";

import { Avatar, Box, Typography } from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";

import { VideoCameraSlashIcon } from "@phosphor-icons/react/dist/ssr";
import type * as Video from "twilio-video";

import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import usePIPDrag from "@/hooks/usePIPDrag";

export enum VideoSize {
  FULL = "full",
  SMALL = "small",
}

interface VideoStreamProps {
  participant: Video.LocalParticipant | Video.RemoteParticipant;
  videoSize?: VideoSize;
  isLocal?: boolean;
  onToggleWhiteboard?: () => void;
  isWhiteboardActive?: boolean;
  excludeTrackNames?: string[];
  pipPosition?: "bottom-right" | "top-right";
  onClick?: () => void;
  preferredTrackType?: "screen" | "camera";
  draggable?: boolean;
}

function VideoStream({
  participant,
  videoSize = VideoSize.FULL,
  isLocal = false,
  onToggleWhiteboard,
  isWhiteboardActive = false,
  excludeTrackNames = [],
  pipPosition = "bottom-right",
  onClick,
  preferredTrackType,
  draggable = false,
}: VideoStreamProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasVideoTrack, setHasVideoTrack] = useState(false);
  const { userRole } = useFirebaseContext();
  const { lesson } = useLesson();

  const excludeTrackNamesMemo = useMemo(() => excludeTrackNames, [excludeTrackNames?.join(',') ?? '']);

  // Main (remote) is full area, local is a small overlay
  const isPIP = isLocal || videoSize === VideoSize.SMALL;
  const isInteractive = isPIP && (isWhiteboardActive || onClick !== undefined);
  const isDraggablePIP = isPIP && draggable;

  // Use PIP drag hook
  const {
    position,
    isDragging,
    handleMouseDown,
    handlePIPClick,
    getDragStyle,
  } = usePIPDrag({
    containerRef,
    isDraggable: isDraggablePIP,
    pipPosition,
    onClick,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTrackAttached = (track: Video.Track) => {
      // Skip whiteboard tracks - we don't want to display them
      if (track.name === "whiteboard") {
        return;
      }

      // Skip excluded track names (e.g., "screen" for camera-only PIP)
      if (excludeTrackNamesMemo.includes(track.name || "")) {
        return;
      }

      // If preferredTrackType is set, only show that type
      if (preferredTrackType && track.kind === "video") {
        const isScreenTrack = track.name === "screen";
        const isCameraTrack = track.name !== "screen" && track.name !== "whiteboard";
        if (preferredTrackType === "screen" && !isScreenTrack) {
          return;
        }
        if (preferredTrackType === "camera" && !isCameraTrack) {
          return;
        }
      }

      // Attach video for all, audio only for remote
      if (track.kind === "video") {
        setHasVideoTrack(true);
        if (track.name === "screen") {
          const screenTrack = track as Video.VideoTrack;
          const el = screenTrack.attach();
          Object.assign(el.style, {
            width: "100%",
            height: "100%",
            objectFit: "contain",
            zIndex: "2",
            backgroundColor: "#000000",
          });
          container.appendChild(el);
        } else {
          const videoTrack = track as Video.VideoTrack;
          const el = videoTrack.attach();
          Object.assign(el.style, {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: "1",
          });
          container.appendChild(el);
        }
      } else if (track.kind === "audio" && !isLocal) {
        const audioTrack = track as Video.AudioTrack;
        const el = audioTrack.attach();
        container.appendChild(el);
      }
    };

    const handleTrackDetached = (track: Video.Track) => {
      // Skip whiteboard tracks for detachment as well
      if (track.name === "whiteboard") {
        return;
      }

      // Skip excluded track names
      if (excludeTrackNamesMemo.includes(track.name || "")) {
        return;
      }

      // Skip if preferredTrackType doesn't match
      if (preferredTrackType && track.kind === "video") {
        const isScreenTrack = track.name === "screen";
        const isCameraTrack = track.name !== "screen" && track.name !== "whiteboard";
        if (preferredTrackType === "screen" && !isScreenTrack) {
          return;
        }
        if (preferredTrackType === "camera" && !isCameraTrack) {
          return;
        }
      }

      if (track.kind === "video" || track.kind === "audio") {
        const t = track as Video.VideoTrack | Video.AudioTrack;
        for (const el of t.detach()) {
          el.remove();
        }
        // Check if any video tracks remain (excluding whiteboard, screen, excluded tracks, and non-preferred types)
        if (track.kind === "video") {
          const tracks = participant.tracks as Map<
            string,
            Video.LocalTrackPublication | Video.RemoteTrackPublication
          >;
          const hasVideo = Array.from(tracks.values()).some(
            (pub) => {
              if (!pub.track || pub.track.kind !== "video") return false;
              if (pub.track.name === "whiteboard") return false;
              if (excludeTrackNamesMemo.includes(pub.track.name || "")) return false;
              if (preferredTrackType) {
                const isScreenTrack = pub.track.name === "screen";
                const isCameraTrack = pub.track.name !== "screen" && pub.track.name !== "whiteboard";
                if (preferredTrackType === "screen" && !isScreenTrack) return false;
                if (preferredTrackType === "camera" && !isCameraTrack) return false;
              }
              return true;
            }
          );
          setHasVideoTrack(hasVideo);
        }
      }
    };

    // Handle local publications
    const handlePublication = (
      publication: Video.LocalTrackPublication | Video.RemoteTrackPublication
    ) => {
      if (publication.track) handleTrackAttached(publication.track);
    };
    const handleUnpublication = (
      publication: Video.LocalTrackPublication | Video.RemoteTrackPublication
    ) => {
      if (publication.track) handleTrackDetached(publication.track);
    };
    participant.on("trackPublished", handlePublication);
    participant.on("trackUnpublished", handleUnpublication);

    // Subscribe to future track events
    participant.on("trackSubscribed", handleTrackAttached);
    participant.on("trackUnsubscribed", handleTrackDetached);

    // Attach existing tracks
    for (const publication of participant.tracks.values()) {
      const track = publication.track;
      if (track) handleTrackAttached(track);
    }

    return () => {
      participant.off("trackSubscribed", handleTrackAttached);
      participant.off("trackUnsubscribed", handleTrackDetached);
      participant.off("trackPublished", handlePublication);
      participant.off("trackUnpublished", handleUnpublication);
      for (const publication of participant.tracks.values()) {
        const track = publication.track;
        if (track) handleTrackDetached(track);
      }
    };
  }, [participant, isLocal, excludeTrackNamesMemo, preferredTrackType]);

  const getPIPStyle = () => {
    const dragStyle = getDragStyle();
    const hasCustomPosition = isDraggablePIP && position;

    return {
      ...dragStyle,
      ...(hasCustomPosition
        ? {}
        : pipPosition === "top-right"
          ? {
            top: 24,
            right: 24
          }
          : {
            bottom: 24,
            right: 24
          }),
    };
  };

  const photoUrl = useMemo(() => {
    return userRole === "tutor" ? lesson?.studentPhotoUrl : lesson?.tutorPhotoUrl;
  }, [userRole, lesson?.studentPhotoUrl, lesson?.tutorPhotoUrl]);

  const executeClickAction = () => {
    if (onClick) {
      onClick();
    } else if (onToggleWhiteboard && isWhiteboardActive) {
      onToggleWhiteboard();
    }
  };

  // Handle click for non-draggable PIPs or whiteboard toggle
  const handleClick = (e: React.MouseEvent) => {
    if (isDraggablePIP) {
      // For draggable PIPs, use the hook's click handler
      handlePIPClick(e);
    } else {
      // For non-draggable PIPs, execute click action directly
      executeClickAction();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      executeClickAction();
    }
  };

  return (
    <Box
      ref={containerRef}
      style={getPIPStyle()}
      onClick={handleClick}
      onMouseDown={isDraggablePIP ? handleMouseDown : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={isInteractive ? 0 : undefined}
      role={isInteractive ? "button" : undefined}
      title={
        isDraggablePIP
          ? "Drag to move, click to toggle view"
          : isInteractive
            ? onClick
              ? "Click to toggle view"
              : "Click to exit whiteboard view"
            : undefined
      }
      sx={{
        overflow: "hidden",
        ...(isPIP
          ? {
            position: "absolute",
            width: 240,
            height: 160,
            bgcolor: "#3B4D4C",
            borderRadius: 1.5,
            zIndex: 10,
            boxShadow: 6,
            cursor: isDragging
              ? "grabbing"
              : isDraggablePIP
                ? "grab"
                : isInteractive
                  ? "pointer"
                  : "default",
            transition: isInteractive && !isDragging ? "transform 0.2s ease, box-shadow 0.2s ease" : undefined,
            "&:hover": isInteractive && !isDragging
              ? {
                transform: "scale(1.05)",
                boxShadow: 8,
                outline: "2px solid",
                outlineColor: "primary.main",
              }
              : undefined,
          }
          : {
            flexGrow: 1,
            width: "100%",
            height: "100%",
            bgcolor: "#0A211F",
          }),
      }}
    >
      {!hasVideoTrack && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            opacity: .6
          }}>
            {isLocal ? (
              <VideoCameraSlashIcon size={28} weight="fill" color="#FFF" aria-hidden />
            ) : photoUrl ? (
              <Avatar
                src={photoUrl}
                alt={
                  userRole === "tutor"
                    ? lesson?.studentName || "No Video"
                    : lesson?.tutorName || "No Video"
                }
                sx={{
                  width: 64,
                  height: 64
                }}
              />
            ) : (
              <PersonIcon sx={{
                color: "common.white",
                fontSize: 40
              }} />
            )}
            <Typography variant="text13L" sx={{ color: "common.white" }}>
              {isLocal
                ? "Camera off"
                : participant.identity
                  ? participant.identity
                  : "No Video"}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default memo(VideoStream);
